import React from 'react';
import { motion } from 'framer-motion';
import {
	ShowcaseContainer,
	Header,
	LogoImage,
	Logo,
	Tagline,
	Section,
	SectionTitle,
	SectionContent,
	VideoContainer,
	DifferentialsGrid,
	DifferentialCard,
	PricingSection,
	PricingTable,
	PricingCard,
	FAQContainer,
	FAQItem,
	HighlightBox,
	StepsContainer,
	StepsGrid,
	StepCenterContainer,
	Step,
	ModernPriceCard,
	BenefitsCard,
	Footer,
	FooterContent,
	ContactButton
} from 'src/pages/partners/styles';

// Variantes de animação para scroll reveal
const fadeInUp = {
	hidden: { opacity: 0, y: 50, },
	visible: { 
		opacity: 1, 
		y: 0,
		transition: { duration: 0.6, },
	},
};

const staggerContainer = {
	hidden: { opacity: 0, },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.2,
		},
	},
};

const scaleIn = {
	hidden: { opacity: 0, scale: 0.8, },
	visible: { 
		opacity: 1, 
		scale: 1,
		transition: { duration: 0.5, },
	},
};

export default function Showcase() {
	return (
		<ShowcaseContainer>
			<Header>
				<motion.div
					initial={{ opacity: 0, y: -20, }}
					animate={{ opacity: 1, y: 0, }}
					transition={{ duration: 0.8, }}
				>
					<LogoImage 
						src={`${process.env.PUBLIC_URL}/intraguard_logo.png`}
						alt="Intraguard Logo"
					/>
					<Logo>Intraguard</Logo>
					<Tagline>Análises e Auditorias de negócios via WhatsApp</Tagline>
				</motion.div>
			</Header>

			{/* O que é o Intraguard */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>O que é o Intraguard?</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionContent>
						<p>
              O Intraguard permite enviar via WhatsApp análises e/ou auditorias de negócios a partir de consultas SQL.
						</p>
						<HighlightBox>
							<p style={{ fontSize: '1.3rem', fontWeight: '700', marginBottom: '1rem', }}>Você, como parceiro, auxiliando o dono no controle do negócio!</p>
						</HighlightBox>
					</SectionContent>
				</motion.div>
			</Section>

			{/* Problema que resolve */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Problema que resolve</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionContent>
						<p>
							Elimine a necessidade de monitoramento manual constante do seu
							sistema ERP fazendo com que informações relevantes cheguem até
							você via WhatsApp, permitindo assim uma rápida delegação de
							responsabilidade, resolução de auditorias e acompanhamento de
							métricas.
						</p>
					</SectionContent>
				</motion.div>
			</Section>

			{/* Casos de Uso Comuns */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Casos de Uso Comuns</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={staggerContainer}
				>
					<DifferentialsGrid>
						<motion.div variants={scaleIn}>
							<DifferentialCard style={{ minHeight: '240px', }}>
								<h3 style={{ fontSize: '1.5rem', }}>Notificação diária/semanal para donos</h3>
								<p style={{ fontSize: '1.2rem', }}>
                  Envie informações relevantes do negócio diretamente para os proprietários.
								</p>
								<p style={{ fontSize: '1.1rem', opacity: 0.75, marginTop: '1rem', }}>
									<em>Ex: Total Vendido no dia, fluxo de caixa simples,
									percentual da meta atingido</em>
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard style={{ minHeight: '240px', }}>
								<h3 style={{ fontSize: '1.5rem', }}>Auditoria segmentada por departamento</h3>
								<p style={{ fontSize: '1.2rem', }}>
                  Alertas frequentes direcionados para grupos específicos de WhatsApp por área.
								</p>
								<p style={{ fontSize: '1.1rem', opacity: 0.75, marginTop: '1rem', }}>
									<em>Ex: Baixa indevida de contas a receber no financeiro,
									verbas vencidas em compras, produtos com entrada e sem venda
									no grupo de vendas</em>
								</p>
							</DifferentialCard>
						</motion.div>
					</DifferentialsGrid>
				</motion.div>
			</Section>

			{/* Como funciona */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Como funciona?</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={scaleIn}
				>
					<VideoContainer>
						<video
							autoPlay
							loop
							muted
							playsInline
							poster={`${process.env.PUBLIC_URL}/intraguard_logo.png`}
							aria-label="Vídeo de demonstração do Intraguard mostrando o sistema em ação"
							style={{
								width: '100%',
								borderRadius: '8px',
								boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
							}}
						>
							<source src={`${process.env.PUBLIC_URL}/promotion_video_intraguard.mp4`} type="video/mp4" />
              Seu navegador não suporta a reprodução de vídeos. Por favor, atualize para um navegador moderno.
						</video>
					</VideoContainer>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.2, }}
					variants={staggerContainer}
				>
					<StepsContainer>
						<StepsGrid>
							<motion.div variants={fadeInUp}>
								<Step>
									<div className="step-number">1</div>
									<div className="step-content">
                    Crie uma consulta ou importe da store
									</div>
								</Step>
							</motion.div>
							<motion.div variants={fadeInUp}>
								<Step>
									<div className="step-number">2</div>
									<div className="step-content">
                    Escreva templates de mensagens
									</div>
								</Step>
							</motion.div>
							<motion.div variants={fadeInUp}>
								<Step>
									<div className="step-number">3</div>
									<div className="step-content">
                    Defina a frequência de execução
									</div>
								</Step>
							</motion.div>
							<motion.div variants={fadeInUp}>
								<Step>
									<div className="step-number">4</div>
									<div className="step-content">
                    Defina para qual grupo de WhatsApp será enviado
									</div>
								</Step>
							</motion.div>
						</StepsGrid>
						<StepCenterContainer>
							<motion.div variants={fadeInUp}>
								<Step>
									<div className="step-number">5</div>
									<div className="step-content">
                    Crie suas regras de notificação/auditoria
									</div>
								</Step>
							</motion.div>
						</StepCenterContainer>
					</StepsContainer>
				</motion.div>
			</Section>

			{/* Como consultores estão monetizando */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Como consultores estão monetizando?</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionContent>
						<p>
              Consultores e parceiros estão gerando mais valor para seus clientes ao fornecer 
              dados e auditorias relevantes e customizáveis através do WhatsApp.
						</p>
					</SectionContent>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={scaleIn}
				>
					<ModernPriceCard>
						<p style={{ fontSize: '1.1rem', marginBottom: '1rem', }}>
              Preço sugerido para o cliente final
						</p>
						<div style={{ 
							fontSize: '3rem', 
							fontWeight: 'bold', 
							color: '#dfae46', 
							margin: '1.5rem 0', 
						}} className="modern-price"
						>
              R$ 500 - R$ 800
						</div>
						<style>{`
              @media (max-width: 768px) {
                .modern-price {
                  font-size: 2rem !important;
                }
              }
            `}</style>
						<p style={{ fontSize: '0.95rem', opacity: 0.9, }}>
              por empresa
						</p>
					</ModernPriceCard>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionContent>
						<p>
              Com o Intraguard, você pode oferecer um serviço diferenciado de monitoramento 
              e alertas personalizados, agregando valor significativo ao seu portfólio de soluções.
						</p>
					</SectionContent>
				</motion.div>
			</Section>

			{/* Diferenciais */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Diferenciais</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.1, }}
					variants={staggerContainer}
				>
					<DifferentialsGrid>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Instalação Simplificada</h3>
								<p>
                  Não precisa liberar porta no firewall do cliente. Basta instalar um client 
                  em qualquer máquina com acesso ao banco de dados.
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Gestão Unificada</h3>
								<p>
                  Gerencie múltiplas organizações de forma centralizada, facilitando a 
                  administração de todos os seus clientes.
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Store Personalizada</h3>
								<p>
                  Consultores conseguem ter sua própria store, compartilhada entre seus clientes, 
                  criando soluções reutilizáveis.
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Store Global</h3>
								<p>
                  Acesso a diversos observadores prontos para ambiente Winthor, economizando 
                  tempo de implementação.
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Compatibilidade</h3>
								<p>
                  Suporte completo para bancos de dados Oracle e PostgreSQL, atendendo 
                  diferentes ambientes.
								</p>
							</DifferentialCard>
						</motion.div>
						<motion.div variants={scaleIn}>
							<DifferentialCard>
								<h3>Sem Limites</h3>
								<p>
                  Alertas/Observadores ilimitados, grupos de WhatsApp ilimitados e 
                  usuários ilimitados.
								</p>
							</DifferentialCard>
						</motion.div>
					</DifferentialsGrid>
				</motion.div>
			</Section>

			{/* Modelo de Distribuição */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Modelo de Distribuição</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionContent>
						<p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '2rem', }}>
							<strong>Distribuição exclusiva via parceiros</strong>
						</p>
					</SectionContent>
				</motion.div>

				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<PricingSection>
						<h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center', color: '#dfae46', }}>
              Preços para Parceiros
						</h3>
						<motion.div
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true, amount: 0.2, }}
							variants={staggerContainer}
						>
							<PricingTable>
								<motion.div variants={scaleIn}>
									<PricingCard>
										<h3>1-5 Empresas</h3>
										<div className="price">R$ 180</div>
										<div className="description">por empresa</div>
									</PricingCard>
								</motion.div>
								<motion.div variants={scaleIn}>
									<PricingCard>
										<h3>6-10 Empresas</h3>
										<div className="price">R$ 170</div>
										<div className="description">por empresa</div>
									</PricingCard>
								</motion.div>
								<motion.div variants={scaleIn}>
									<PricingCard>
										<h3>11-20 Empresas</h3>
										<div className="price">R$ 160</div>
										<div className="description">por empresa</div>
									</PricingCard>
								</motion.div>
								<motion.div variants={scaleIn}>
									<PricingCard>
										<h3>21+ Empresas</h3>
										<div className="price">R$ 150</div>
										<div className="description">por empresa</div>
									</PricingCard>
								</motion.div>
							</PricingTable>
						</motion.div>
					</PricingSection>
				</motion.div>
			</Section>

			{/* Benefícios para Parceiros */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Benefícios para Parceiros</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={scaleIn}
				>
					<BenefitsCard>
						<ul>
							<li>Criar e gerenciar empresas clientes</li>
							<li>Criar usuários para as organizações</li>
							<li>Acessar os observadores das organizações sob sua gestão</li>
							<li>Criar sua própria store e publicar observadores personalizados</li>
							<li>Descontos progressivos baseados no volume de clientes</li>
						</ul>
					</BenefitsCard>
				</motion.div>
			</Section>

			{/* FAQ */}
			<Section>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<SectionTitle>Perguntas Frequentes</SectionTitle>
				</motion.div>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.1, }}
					variants={staggerContainer}
				>
					<FAQContainer>
						<motion.div variants={fadeInUp}>
							<FAQItem>
								<h3>Preciso ter conhecimento técnico para usar o Intraguard?</h3>
								<p>
                  Conhecimento básico em SQL é necessário para criar observadores personalizados. 
                  No entanto, a store oferece diversos observadores prontos que podem ser utilizados 
                  sem necessidade de programação.
								</p>
							</FAQItem>
						</motion.div>
						<motion.div variants={fadeInUp}>
							<FAQItem>
								<h3>Quantos grupos de WhatsApp posso criar?</h3>
								<p>
                  Não há limite! Você pode criar quantos grupos de WhatsApp precisar para organizar 
                  as notificações por departamento, tipo de alerta ou qualquer outra categorização 
                  que faça sentido para seu negócio.
								</p>
							</FAQItem>
						</motion.div>
						<motion.div variants={fadeInUp}>
							<FAQItem>
								<h3>O Intraguard funciona com qualquer ERP?</h3>
								<p>
                  O Intraguard funciona com qualquer sistema que utilize banco de dados Oracle ou 
                  PostgreSQL. Temos uma store especializada em observadores para o ERP Winthor, mas 
                  você pode criar observadores personalizados para qualquer sistema.
								</p>
							</FAQItem>
						</motion.div>
						<motion.div variants={fadeInUp}>
							<FAQItem>
								<h3>Como funciona a instalação?</h3>
								<p>
                  A instalação é extremamente simples. Basta instalar o client do Intraguard em uma 
                  máquina que tenha acesso ao banco de dados. Não é necessário fazer configurações 
                  complexas de firewall ou NAT.
								</p>
							</FAQItem>
						</motion.div>
						<motion.div variants={fadeInUp}>
							<FAQItem>
								<h3>Posso testar antes de comprar?</h3>
								<p>
                  Entre em contato com nosso time comercial para agendar uma demonstração completa 
                  da plataforma e discutir as melhores opções para o seu caso.
								</p>
							</FAQItem>
						</motion.div>
					</FAQContainer>
				</motion.div>
			</Section>

			{/* Rodapé */}
			<Footer>
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.3, }}
					variants={fadeInUp}
				>
					<FooterContent>
						<h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#dfae46', }}>
              Eleve o valor da sua entrega a partir de hoje
						</h2>
						<p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.9, }}>
              Entre em contato com nosso time comercial e descubra como o Intraguard 
              pode agregar valor ao seu negócio.
						</p>
						<ContactButton
							as="a"
							href="https://wa.me/5562999060584?text=Olá,%20vim%20pelo%20site!%20Gostaria%20de%20saber%20mais%20sobre%20o%20Intraguard."
							target="_blank"
							rel="noopener noreferrer"
							whileHover={{ scale: 1.05, }}
							whileTap={{ scale: 0.95, }}
						>
              Fale com nosso time
						</ContactButton>
					</FooterContent>
				</motion.div>
			</Footer>
		</ShowcaseContainer>
	);
}
